import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, ShieldCheck, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import SEO from "../../components/SEO";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(true);

  // Auth is now cookie-driven. If /auth/me succeeds, jump straight to inbox.
  // Otherwise show the login form. The legacy localStorage token is only
  // used to short-circuit /auth/me — the real auth check is server-side.
  useEffect(() => {
    api
      .get("/auth/me")
      .then(() => navigate("/admin/forfragningar", { replace: true }))
      .catch(() => setChecking(false));
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.includes("@")) {
      setError("Ange en giltig e-postadress");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/request-link", { email });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Kunde inte skicka länk just nu");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-[80vh] flex items-center justify-center px-4 py-16 bg-[#F7F5F0]"
    >
      <SEO
        title="Logga in — admin | Trädfällarna"
        description="Adminläge för Trädfällarna Laholm AB."
        path="/admin"
        noindex
      />
      <div className="w-full max-w-md bg-white border border-[#E2E0D8] rounded-xl p-8 shadow-sm">
        {checking ? (
          <p
            data-testid="admin-login-checking"
            className="text-center text-[#5A6258] py-8"
          >
            Kontrollerar inloggning…
          </p>
        ) : (
          <>
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#1B2A1E] text-[#D95D2E] mb-5">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h1 className="font-heading text-3xl font-extrabold text-[#1B2A1E] mb-2">
              Logga in
            </h1>
            <p className="text-[#3a4338] mb-6">
              Vi mejlar en inloggningslänk till adresser som har behörighet.
            </p>

            {sent ? (
          <div
            data-testid="admin-login-sent"
            className="bg-[#EFEBDF] border border-[#D95D2E]/30 rounded-lg p-5"
          >
            <h2 className="font-heading font-bold text-lg text-[#1B2A1E] mb-1">
              Kolla din e-post
            </h2>
            <p className="text-[#3a4338] text-sm">
              Vi har skickat en länk till{" "}
              <strong className="text-[#1B2A1E]">{email}</strong>. Klicka på
              länken för att logga in. Den är giltig i 15 minuter.
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setEmail("");
              }}
              className="mt-4 text-sm text-[#D95D2E] font-semibold hover:underline"
            >
              Skicka till en annan adress
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="admin-login-form" noValidate>
            <label className="block">
              <span className="text-sm font-heading font-semibold text-[#1B2A1E] mb-1.5 block">
                E-postadress
              </span>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5A6258]" />
                <input
                  type="email"
                  data-testid="admin-login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="din@adress.se"
                  className="w-full h-12 pl-11 pr-4 rounded-lg bg-white border border-[#E2E0D8] focus:outline-none focus:ring-2 focus:ring-[#D95D2E] focus:border-transparent"
                />
              </div>
            </label>
            {error && (
              <p
                data-testid="admin-login-error"
                className="mt-3 text-sm text-red-600"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors disabled:opacity-60"
            >
              {submitting ? "Skickar…" : "Skicka inloggningslänk"}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
            <p className="mt-5 text-xs text-[#5A6258] leading-relaxed">
              Endast godkända e-postadresser kan logga in. Om du försöker logga
              in från en oväntad adress hör av dig till oss.
            </p>
          </form>
        )}
          </>
        )}
      </div>
    </div>
  );
}
